import React from "react";
import { Box, MenuItem, Select } from "@mui/material";
import moment from "moment";

export const TREND_PERIODS = {
  YEARLY: "YEARLY",
  MONTHLY: "MONTHLY",
  WEEKLY: "WEEKLY",
  DAILY: "DAILY",
};

const MONTH_OPTIONS = moment.months().map((label, index) => ({
  label,
  value: index + 1,
}));

const selectSx = {
  width: "max-content",
  maxWidth: "100%",
  flex: "0 0 auto",
  bgcolor: "#fff",
  fontSize: 13,
  "& .MuiSelect-select": {
    py: 1,
    pr: "32px !important",
    width: "auto",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};

/**
 * Past 6 calendar years including the current year (newest first).
 */
export function getTrendYearOptions(referenceDate = new Date()) {
  const current = moment(referenceDate).year();
  return Array.from({ length: 6 }, (_, i) => current - i);
}

/**
 * Build week filter options from WEEKLY trends `series`.
 * Uses BE `label` (e.g. "W1") and parses week number from `key` / `label`.
 */
export function parseWeekOptionsFromSeries(series = []) {
  return series
    .map((item, index) => {
      const fromKey = String(item?.key ?? "").match(/W(\d+)\s*$/i);
      const fromLabel = String(item?.label ?? "").match(/W(\d+)\s*$/i);
      const week = Number(fromKey?.[1] || fromLabel?.[1] || index + 1);
      if (!Number.isFinite(week) || week < 1) return null;
      return {
        week,
        label: item?.label || `W${week}`,
        key: item?.key ?? null,
      };
    })
    .filter(Boolean);
}

/**
 * Derive API period from how deep the user drilled:
 * year only → MONTHLY, year+month → WEEKLY, year+month+week → DAILY, else YEARLY.
 */
export function resolveTrendPeriod({ year, month, week } = {}) {
  if (year && month && week) return TREND_PERIODS.DAILY;
  if (year && month) return TREND_PERIODS.WEEKLY;
  if (year) return TREND_PERIODS.MONTHLY;
  return TREND_PERIODS.YEARLY;
}

/**
 * Strict param builder for GET /admin/cp/organizations/invoice-trends.
 */
export function buildInvoiceTrendsQuery(organizationId, period, opts = {}) {
  const query = { organizationId, period };

  if (period === TREND_PERIODS.MONTHLY && opts.year) {
    query.year = String(opts.year);
  }
  if (period === TREND_PERIODS.WEEKLY) {
    query.year = String(opts.year);
    query.month = String(opts.month);
  }
  if (period === TREND_PERIODS.DAILY) {
    query.year = String(opts.year);
    query.month = String(opts.month);
    query.week = String(opts.week);
  }

  return query;
}

export function canFetchInvoiceTrends(period, opts = {}) {
  if (period === TREND_PERIODS.YEARLY) return true;
  if (period === TREND_PERIODS.MONTHLY) return Boolean(opts.year);
  if (period === TREND_PERIODS.WEEKLY) return Boolean(opts.year && opts.month);
  if (period === TREND_PERIODS.DAILY) return Boolean(opts.year && opts.month && opts.week);
  return false;
}

/**
 * Human-readable preview of the active trend filter.
 */
export function buildTrendSelectionPreview({ year, month, week, weekOptions = [] } = {}) {
  if (!year) {
    return {
      period: TREND_PERIODS.YEARLY,
      title: "All time",
      subtitle: "Year-over-year overview (last 6 years)",
    };
  }

  const monthName = month ? moment().month(month - 1).format("MMMM") : null;
  const weekMeta = week ? weekOptions.find((w) => w.week === week) : null;

  if (year && month && week) {
    return {
      period: TREND_PERIODS.DAILY,
      title: `${year} · ${monthName} · ${weekMeta?.label || `W${week}`}`,
      subtitle: "Daily breakdown",
    };
  }

  if (year && month) {
    return {
      period: TREND_PERIODS.WEEKLY,
      title: `${year} · ${monthName}`,
      subtitle: weekOptions.length ? `Weeks: ${weekOptions.map((w) => w.label).join(", ")}` : `Weeks in ${monthName}`,
    };
  }

  return {
    period: TREND_PERIODS.MONTHLY,
    title: String(year),
    subtitle: `All 12 months of ${year}`,
  };
}

/**
 * Calendar cascade: Year → Month → Week.
 * Week options are BE-driven (WEEKLY series labels). Always includes "All weeks".
 */
const TrendPeriodFilter = ({
  year,
  month,
  week,
  weekOptions = [],
  onYearChange,
  onMonthChange,
  onWeekChange,
}) => {
  const yearOptions = React.useMemo(() => getTrendYearOptions(), []);

  // Guard Select value so MUI doesn't warn if cached week is gone after month change.
  const weekValue = weekOptions.some((w) => w.week === week) ? week : "";

  return (
    <Box display="inline-flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ maxWidth: "100%" }}>
      <Select
        value={year ?? ""}
        onChange={(e) => {
          const next = e.target.value === "" ? null : Number(e.target.value);
          onYearChange(next);
        }}
        size="small"
        sx={selectSx}
        displayEmpty
      >
        <MenuItem value="">All time</MenuItem>
        {yearOptions.map((y) => (
          <MenuItem key={y} value={y}>
            {y}
          </MenuItem>
        ))}
      </Select>

      {year ? (
        <Select
          value={month ?? ""}
          onChange={(e) => {
            const next = e.target.value === "" ? null : Number(e.target.value);
            onMonthChange(next);
          }}
          size="small"
          sx={selectSx}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) return "All months";
            return MONTH_OPTIONS.find((opt) => opt.value === selected)?.label || selected;
          }}
        >
          <MenuItem value="">All months</MenuItem>
          {MONTH_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      ) : null}

      {year && month ? (
        <Select
          value={weekValue}
          onChange={(e) => {
            const next = e.target.value === "" ? null : Number(e.target.value);
            onWeekChange(next);
          }}
          size="small"
          sx={selectSx}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) return "All weeks";
            const meta = weekOptions.find((w) => w.week === selected);
            return meta?.label || `W${selected}`;
          }}
        >
          <MenuItem value="">All weeks</MenuItem>
          {weekOptions.map((w) => (
            <MenuItem key={w.key || w.week} value={w.week}>
              {w.label}
            </MenuItem>
          ))}
        </Select>
      ) : null}
    </Box>
  );
};

export default TrendPeriodFilter;
